import { useEffect, useRef, useState } from 'react';
import { PersonTypes } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails } from '@frontend/insurance-verification';
import { useAppScopeStore } from '@frontend/scope';
import { WivPromotionCard } from '@frontend/self-upgrade';
import { useContactPanelShallowStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Heading, useModalControl } from '@frontend/design-system';
import { History } from './components/history';
import { Insurance } from './components/insurance';
import { InsuranceBenefitSummary } from './components/insurance-benefit-summary';
import { InsuranceLoader } from './components/insurance-loader';
import NoInsuranceAccess from './components/no-insurance-access';
import { StyledCardContainer } from './components/styled-card-container';
import useGetInsuranceDetails from './hooks/use-get-insurance-details';
import { useShouldShowInsurance } from './hooks/use-should-show-insurance';
import { PersonInfoStoreProvider } from './providers';
import { DateRangeOptionValue } from './utils/constants';
import { UndefinedOrNull } from './utils/types';
import { getNoInsuranceAccessMessage, hasInNetworkOrOutNetworkData } from './utils/utils';

interface Props {
  locationId: string;
  personInfo: UndefinedOrNull<PersonTypes.Person>;
}
export const InsuranceContainer = ({ locationId, personInfo }: Props) => {
  const ref = useRef<HTMLElement>(null);
  const { t } = useTranslation();

  const verificationMetricModalControl = useModalControl();
  const { accessibleLocationData, selectedLocationIds } = useAppScopeStore();
  const { locationIdWiseCustomizationFlagDetails } = useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails();
  const isEnhancedInsuranceVerificationEnabled = locationIdWiseCustomizationFlagDetails?.[locationId!] || false;

  const [defaultDateRange, setDefaultDateRange] = useState<DateRangeOptionValue>('today');
  const [forceFetch, setForceFetch] = useState(false);
  const {
    isLoading: isWeaveTeaserLoading,
    showInsurance,
    showNoInsuranceAccessMessage,
    showWeaveVerifyPromotion,
  } = useShouldShowInsurance(locationId);

  const {
    isLoading: isInsuranceDetailsLoading,
    insuranceDetails,
    hasActiveOrVerifiedInsurance,
    hasErrors,
    hasCoverage,
    isInsuranceDetailsWritebackSupported,
    isAutoVerification,
    requestId,
    personDetailsCoverage,
  } = useGetInsuranceDetails(locationId, personInfo as PersonTypes.Person, forceFetch);

  const { scrollTo, setScrollTo } = useContactPanelShallowStore('scrollTo', 'setScrollTo');

  useEffect(() => {
    if (scrollTo === 'insurance-details') {
      ref.current?.scrollIntoView({ behavior: 'smooth' });
      setScrollTo(undefined);
    }
  }, [scrollTo]);

  const isLoading = isInsuranceDetailsLoading || isWeaveTeaserLoading;
  const locationData = accessibleLocationData[locationId!];
  const isDemoOrDevOffice = locationData?.opsType === 'DEV' || locationData?.opsType === 'DEMO';
  const hasHistoryData = insuranceDetails?.historyData && Object.keys(insuranceDetails.historyData).length > 0;
  const showInNetworkOrOutNetworkData = hasInNetworkOrOutNetworkData(insuranceDetails?.benefitLimits);

  const updateDefaultDateRange = (range: DateRangeOptionValue) => {
    setDefaultDateRange(range);
    verificationMetricModalControl.openModal();
  };

  if (!showInsurance) return null;

  if (isLoading) {
    return <InsuranceLoader />;
  } else if (showNoInsuranceAccessMessage) {
    /**
          This message is shown only if it's a multi-location setup where child locations
          do have insurance access, but the current specific location does not.
       */
    const noAccessMessage = getNoInsuranceAccessMessage({
      accessibleLocationsData: accessibleLocationData,
      currentLocationId: locationId,
      selectedLocationIds,
      t,
    });

    return <NoInsuranceAccess message={noAccessMessage} t={t} />;
  } else if (showWeaveVerifyPromotion) {
    return (
      <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1), padding: theme.spacing(2) }}>
        <Heading level={3}>{t('Insurance')}</Heading>
        <WivPromotionCard locationIds={[locationId]} source='contacts' />
      </div>
    );
  }
  return (
    <>
      <PersonInfoStoreProvider personInfo={personInfo as PersonTypes.Person} locationId={locationId}>
        <StyledCardContainer>
          <Insurance
            ref={ref}
            locationId={locationId}
            personId={personInfo?.PersonID || ''}
            insuranceDetails={insuranceDetails}
            requestId={requestId}
            isEnhancedInsuranceVerificationEnabled={isEnhancedInsuranceVerificationEnabled}
            isDemoOrDevOffice={isDemoOrDevOffice}
            hasErrors={hasErrors}
            hasCoverage={hasCoverage}
            hasActiveOrVerifiedInsurance={hasActiveOrVerifiedInsurance}
            isInsuranceDetailsWritebackSupported={isInsuranceDetailsWritebackSupported}
            isAutoVerification={isAutoVerification}
            personDetailsCoverage={personDetailsCoverage}
            defaultDateRange={defaultDateRange}
            setForceFetch={setForceFetch}
            updateDefaultDateRange={updateDefaultDateRange}
          />
        </StyledCardContainer>
      </PersonInfoStoreProvider>
      {showInNetworkOrOutNetworkData && (
        <StyledCardContainer>
          <InsuranceBenefitSummary benefitDetails={insuranceDetails?.benefitLimits} />
        </StyledCardContainer>
      )}

      {hasHistoryData && (
        <StyledCardContainer>
          <History stepsData={insuranceDetails?.historyData} />
        </StyledCardContainer>
      )}
    </>
  );
};
InsuranceContainer.displayName = 'InsuranceContainer';
